import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Gift, ArrowRightLeft, History, X, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

const BENEFIT_META = {
  entry_gift: { label: "Entry Gift Bonus", emoji: "🎁" },
  lucky_draw_cashback: { label: "Lucky Draw Cashback", emoji: "🏆" },
  first_premium_cashback: { label: "First Premium Cashback", emoji: "💰" },
  referral_income: { label: "Referral Income", emoji: "🤝" },
  special_agent_share: { label: "Special Agent Share", emoji: "⭐" },
  premium_finance_support: { label: "Finance Support Reward", emoji: "💼" },
};

export default function BonusWallet() {
  const nav = useNavigate();
  const [bal, setBal] = useState(0);
  const [b, setB] = useState({});
  const [tx, setTx] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferAmt, setTransferAmt] = useState("");
  const [transferring, setTransferring] = useState(false);

  const load = async () => {
    const w = await api.get("/customer/wallet");
    setBal(w.data.bonus_balance);
    const r = await api.get("/customer/benefits");
    setB(r.data);
    const t = await api.get("/customer/transactions?wallet=bonus");
    setTx(t.data);
  };
  useEffect(() => { load(); }, []);

  const submitTransfer = async () => {
    const amt = parseFloat(transferAmt);
    if (!amt || amt <= 0) { toast.error("Enter valid amount"); return; }
    if (amt > bal) { toast.error("Insufficient bonus balance"); return; }
    setTransferring(true);
    try {
      await api.post("/customer/wallet/transfer-bonus-to-saving", { amount: amt });
      toast.success(`${fmtINR(amt)} transferred to Saving Wallet`);
      setTransferOpen(false); setTransferAmt(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setTransferring(false); }
  };

  const visibleTx = showAll ? tx : tx.slice(0, 6);

  return (
    <div data-testid="bonus-wallet-page">
      <div className="bg-tenant-gradient text-white px-5 pt-6 pb-20 rounded-b-[2rem] relative">
        <div className="flex items-center gap-3">
          <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="font-extrabold tracking-tight text-lg">Bonus Wallet</div>
        </div>
      </div>

      <div className="px-5 -mt-12 relative z-10">
        <div className="bonus-wallet-grad text-white rounded-3xl p-6 card-shadow-lg">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] opacity-90">
            <Gift className="w-3.5 h-3.5" /> Bonus Wallet
          </div>
          <div className="mt-3 text-[11px] opacity-80">Available Balance</div>
          <div data-testid="bonus-balance" className="text-3xl font-extrabold tracking-tight mt-1">{fmtINR(bal)}</div>
          <div className="text-[11px] opacity-80 mt-2">Rewards & Benefits only</div>

          <button
            data-testid="transfer-to-saving-btn"
            onClick={() => setTransferOpen(true)}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-white text-violet-700 py-3 rounded-2xl font-bold text-sm tap-scale">
            <ArrowRightLeft className="w-4 h-4" /> Transfer to Saving Wallet
          </button>
        </div>
      </div>

      <div className="px-5 mt-6">
        <h3 className="font-extrabold tracking-tight mb-3">Bonus Breakdown</h3>
        <div className="space-y-2.5">
          {Object.entries(BENEFIT_META).map(([k, meta]) => (
            <div key={k} className="bg-white border border-slate-100 rounded-2xl p-3.5 flex items-center justify-between card-shadow">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-violet-50 grid place-items-center text-lg">{meta.emoji}</div>
                <div className="text-sm font-bold">{meta.label}</div>
              </div>
              <div data-testid={`benefit-${k}`} className="font-extrabold text-violet-700">{fmtINR(b[k] || 0)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="px-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-extrabold tracking-tight inline-flex items-center gap-2">
            <History className="w-4 h-4" /> Transaction History
          </h3>
          <span className="text-[11px] text-slate-400 font-bold">{tx.length} total</span>
        </div>
        <div className="space-y-2.5">
          {visibleTx.map((t) => (
            <div key={t.id} data-testid={`bonus-tx-${t.id}`} className="bg-white border border-slate-100 rounded-2xl p-3 flex items-center justify-between card-shadow">
              <div>
                <div className="font-bold text-sm capitalize">{t.category.replaceAll("_", " ")}</div>
                <div className="text-[11px] text-slate-400">{fmtDate(t.created_at)}</div>
                {t.note && <div className="text-[11px] text-slate-500 mt-0.5">{t.note}</div>}
              </div>
              <div className={`font-extrabold ${t.type === "credit" ? "text-emerald-600" : "text-rose-500"}`}>
                {t.type === "credit" ? "+" : "-"}{fmtINR(t.amount)}
              </div>
            </div>
          ))}
          {tx.length === 0 && <div className="text-center text-slate-400 text-sm py-10">No bonus activity yet.</div>}
        </div>
        {tx.length > 6 && (
          <button data-testid="toggle-show-all" onClick={() => setShowAll(!showAll)} className="mt-3 w-full text-center text-sm font-bold text-tenant-primary py-2">
            {showAll ? "Show less" : `View all ${tx.length} transactions`}
          </button>
        )}
      </div>

      {transferOpen && (
        <div data-testid="transfer-modal" className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setTransferOpen(false)}>
          <div className="bg-white rounded-3xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-extrabold text-lg">Transfer to Saving Wallet</div>
              <button onClick={() => setTransferOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-slate-500 mt-1">Bonus available: <b>{fmtINR(bal)}</b></div>
            <label className="mt-3 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Amount</label>
            <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-4 py-3">
              <IndianRupee className="w-5 h-5 text-slate-400" />
              <input data-testid="transfer-amount" value={transferAmt} onChange={(e) => setTransferAmt(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0" inputMode="decimal" className="flex-1 outline-none text-xl font-extrabold" />
              <button onClick={() => setTransferAmt(String(bal))} className="text-[11px] bg-slate-100 text-slate-600 font-bold px-2 py-1 rounded">MAX</button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => setTransferOpen(false)} className="py-3 rounded-xl border border-slate-200 font-bold">Cancel</button>
              <button data-testid="confirm-transfer" onClick={submitTransfer} disabled={transferring} className="py-3 rounded-xl bg-violet-600 text-white font-bold disabled:opacity-60">{transferring ? "Transferring..." : "Confirm"}</button>
            </div>
          </div>
        </div>
      )}

      <div className="h-10" />
    </div>
  );
}
